// The plain names of result types (REQ-260 AC1: "Purchases, Leads, Messages started, Link clicks, …") and the
// REQ-043 AC2 rule that, where one result type applies, the word "result" is replaced by its name: "Results" →
// "Purchases", "Cost per result" → "Cost per purchase". `leads` reads "Sign-ups" because DEMO_STORY, the personas
// and the QA fixtures say "47 sign-ups" / "Cost per sign-up" (REQ-043 AC2), never "leads".
//
// Lifted from `apps/web/src/explainers/resultNouns.ts` (T-045) in T-020: the renderer needs these nouns for
// every `resultType` param and every `count` whose unit is a result type, and mobile needs the same words.

import type { ResultTypeKey } from '../types/entities';

/**
 * Three forms of one noun. **Which one you want is decided by where it goes, not by how many there are:**
 *
 * | form | where | example |
 * |---|---|---|
 * | `plural` | a COLUMN HEAD, a tab, a menu entry, a chip — anywhere the noun stands alone as a label | `Purchases` |
 * | `plurals` | inside a SENTENCE or beside a figure, however large the figure | `13 purchases`, `with no purchases` |
 * | `singular` | "Cost per <singular>", and a sentence about exactly one | `Cost per purchase` |
 *
 * `plural` and `plurals` differ only in case, so **`plural` type-checks perfectly where `plurals` is meant** and
 * renders "13 Purchases" in the middle of a sentence. That is the easiest mistake in this package (T-097 got it
 * wrong three times in four); if the noun has a number in front of it or a verb behind it, it is `plurals`.
 */
export interface ResultNoun {
  /** Title-case plural, the LABEL form — a column head, never inside a sentence: "Purchases", "Messages started". */
  plural: string;
  /** Lower-case singular for "Cost per <singular>": "purchase", "message started". */
  singular: string;
  /** Lower-case plural, the SENTENCE form — what follows a count: "purchases", "messages started". */
  plurals: string;
}

export const RESULT_NOUNS: Readonly<Record<ResultTypeKey, ResultNoun>> = {
  purchases: { plural: 'Purchases', singular: 'purchase', plurals: 'purchases' },
  leads: { plural: 'Sign-ups', singular: 'sign-up', plurals: 'sign-ups' },
  messages: {
    plural: 'Messages started',
    singular: 'message started',
    plurals: 'messages started',
  },
  link_clicks: { plural: 'Link clicks', singular: 'link click', plurals: 'link clicks' },
  landing_page_views: {
    plural: 'Landing page views',
    singular: 'landing page view',
    plurals: 'landing page views',
  },
  video_views: { plural: 'Video views', singular: 'video view', plurals: 'video views' },
  post_engagement: {
    plural: 'Post engagement',
    singular: 'post engagement',
    plurals: 'post engagement',
  },
  app_installs: { plural: 'App installs', singular: 'app install', plurals: 'app installs' },
  reach: { plural: 'People reached', singular: '1,000 people reached', plurals: 'people reached' },
  registrations: { plural: 'Registrations', singular: 'registration', plurals: 'registrations' },
  add_to_cart: { plural: 'Adds to cart', singular: 'add to cart', plurals: 'adds to cart' },
  checkouts: { plural: 'Checkouts', singular: 'checkout', plurals: 'checkouts' },
  subscriptions: { plural: 'Subscriptions', singular: 'subscription', plurals: 'subscriptions' },
  contacts: { plural: 'Contacts', singular: 'contact', plurals: 'contacts' },
  page_likes: { plural: 'Page likes', singular: 'page like', plurals: 'page likes' },
  // D-397 (T-286): Meta's "Event responses" — Going OR Interested to a Facebook event; "RSVPs" was rejected
  event_responses: {
    plural: 'Event responses',
    singular: 'event response',
    plurals: 'event responses',
  },
  other: { plural: 'Results', singular: 'result', plurals: 'results' },
  mixed: { plural: 'Mixed', singular: 'result', plurals: 'results' },
};

export const RESULT_TYPE_KEYS = Object.keys(RESULT_NOUNS) as readonly ResultTypeKey[];

export function isResultTypeKey(value: string): value is ResultTypeKey {
  return Object.prototype.hasOwnProperty.call(RESULT_NOUNS, value);
}

export function resultNoun(type: ResultTypeKey): ResultNoun {
  return RESULT_NOUNS[type];
}

/**
 * REQ-043 AC2: a metric label with "result(s)" replaced by the result's name — "Cost per result" → "Cost per
 * purchase", "Results" → "Purchases", "Value of results" → "Value of purchases", "Share of spend among campaigns
 * that aim for {result}" → "… aim for purchases". `other` and `mixed` keep the word "result"; no type keeps the
 * label as the registry has it, with "{result}" read as "the same result".
 */
export function withResultNoun(label: string, type: ResultTypeKey | undefined): string {
  if (!type || type === 'other' || type === 'mixed')
    return label.replace('{result}', 'the same result');
  const noun = RESULT_NOUNS[type];
  return label
    .replace('{result}', noun.plurals)
    .replace(/\bResults\b/g, noun.plural)
    .replace(/\bresults\b/g, noun.plurals)
    .replace(/\bResult\b/g, capitalize(noun.singular))
    .replace(/\bresult\b/g, noun.singular);
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
